/* eslint-disable @typescript-eslint/naming-convention */

import React, { createContext, useContext } from 'react';
import type { Events, ListPageEntry, RemoteModuleRegistry } from '@dutchiesdk/ecommerce-extensions-sdk';

type FallbackProps = { fallback?: React.FC<any> };
type DefaultComponentSig = (options?: FallbackProps) => React.FC<any>;
type CategoryIterable = { category: ListPageEntry['category']; components: React.ElementType[] };

export type RouteablePage = {
  path: string;
  component: DefaultComponentSig;
};

export type CommerceComponents = {
  components: {
    [K in keyof Omit<
      RemoteModuleRegistry,
      | 'CategoryPageInterstitials'
      | 'CategoryPageSlots'
      | 'events'
      | 'getStoreFrontMetaFields'
      | 'RouteablePages'
      | 'StoreFrontCarouselInterstitials'
    >]-?: DefaultComponentSig;
  };
  findPage: (o: { fallback?: React.FC; path?: string }) => React.FC;
  iterables: {
    StoreFrontCarouselInterstitials: () => React.ElementType[];
    CategoryPageInterstitials: () => CategoryIterable[];
    CategoryPageSlots: () => CategoryIterable[];
  };
  pages: ({ fallback }: FallbackProps) => RouteablePage[];
  events: Events | undefined;
};

const EmptyComponent: React.FC<any> = () => <></>;

export const defaultCommerceComponents: CommerceComponents = {
  components: {
    StoreFrontHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontNavigation: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontFooter: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    GlobalStyles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoyaltyWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoginWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ListPageHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsPrimary: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCardListItem: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    FilterSidePanel: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
  },
  findPage: ({ fallback = () => <></> }) => fallback,
  iterables: {
    StoreFrontCarouselInterstitials: () => [],
    CategoryPageInterstitials: () => [],
    CategoryPageSlots: () => [],
  },

  pages: (_props: FallbackProps = { fallback: () => <></> }) => [],
  events: {},
};

export const CommerceComponentsContext = createContext<CommerceComponents>(defaultCommerceComponents);

export const useCommerceComponents = (): CommerceComponents => useContext(CommerceComponentsContext);
